import {
  Button,
  EllipsisSpan,
  FieldTable,
  Form,
  FormContent,
  Input,
  Paging,
  PopConfirm,
  Select,
  Tooltip,
  useBreadcrumbRoutes,
  usePaging,
  Wrapper,
} from '@maxtropy/components';
import { Cascader, Col, Space } from 'antd';
import { useRequest } from 'ahooks';
import { Key, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  apiV2WaterFlowConfigTreePost,
  apiV2DevicePagePost,
  apiV2WaterFlowConfigDetailPost,
  apiV2WaterFlowConfigSavePost,
  V2WaterFlowConfigSavePostRequest,
} from '@maxtropy/device-customer-apis-v2';
import UnitModal from './UnitModal';
import { InfoCircleOutlined, MinusCircleOutlined, PlusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { Device } from '@/api/device';
import { adaptToDeviceTypeTree, DeviceTypeTree, formatOptionData, getChildNodesByParentIds } from './utils';

// 生成 0-24 小时选项
const hourOptions = Array.from({ length: 25 }, (_, i) => ({
  label: `${i.toString().padStart(2, '0')}:00`,
  value: i,
}));

export type DeviceListItem = Partial<Omit<Device, 'rootCode' | 'deviceId'>>;
interface searchParams {
  codeOrName?: string;
  typeId?: Array<Array<number>> | undefined;
}
interface Params {
  codeOrName?: string;
  typeId?: number[];
}

const columns = [
  {
    title: '设备编号',
    dataIndex: 'code',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
  {
    title: '设备名称',
    dataIndex: 'name',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
  {
    title: '设备S/N码',
    dataIndex: 'sn',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
  {
    title: '所属类目',
    dataIndex: 'typeName',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
  {
    title: '设备标签',
    dataIndex: 'tags',
    ellipsis: { showTitle: true },
    render: (v?: string[]) => <EllipsisSpan value={v ? v.join(',') : '--'} />,
  },
  {
    title: '所属组织',
    dataIndex: 'customerName',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
];

const getValidDevices = (devices?: Array<DeviceListItem | undefined>) =>
  (devices ?? []).filter((item): item is DeviceListItem => item?.id !== undefined && item.id !== null);

const LongFlowMonitoringConfig = () => {
  const buildColumns = [
    ...columns,
    {
      title: '操作',
      width: 180,
      render: (record: any) => {
        return (
          <PopConfirm title="!确认删除？删除后不可恢复，你还要继续吗？" onConfirm={() => deleteConfig(record.id)}>
            <Button type="link">移除</Button>
          </PopConfirm>
        );
      },
    },
  ];
  const breadcrumbRoutes = useBreadcrumbRoutes();
  const pagingInfo = usePaging();

  const { pageOffset } = pagingInfo;
  const [showModal, setShowModal] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);
  const [tableSelectedRowKeys, setTableSelectedRowKeys] = useState<Key[]>([]);
  const [form] = Form.useForm();
  const [tableDataSource, setTableDataSource] = useState<DeviceListItem[]>();
  const [allData, setAllData] = useState<DeviceListItem[]>();
  const [searchText, setSearchText] = useState<string>();
  const [selectedTypeIds, setSelectedTypeIds] = useState<number[][]>();
  const [loading, setLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const navigate = useNavigate();

  //配置详情
  useEffect(() => {
    apiV2WaterFlowConfigDetailPost({}).then(res => {
      if (res.deviceIds.length > 0) {
        setSelectedRowKeys(res.deviceIds);
        apiV2DevicePagePost({
          ids: res.deviceIds,
          page: pageOffset,
          size: 200,
        }).then(res => {
          const devices = res.list;
          setAllData(devices);
          setTableDataSource(devices);
        });
      }
      form.setFieldsValue({
        description: res.description ?? '长流水',
        // 将 timePeriods 转换为 [startHour, endHour] 格式
        timePeriods: res.timePeriods?.map((period: any) => {
          const startHour = parseInt(period.monitorStartTime.split(':')[0], 10);
          const endHour = parseInt(period.monitorEndTime.split(':')[0], 10);
          return [startHour, endHour];
        }) || [[null, null]],
      });
    });
  }, []);

  useEffect(() => {
    const filtered = allData?.filter(item => {
      const lowerSearch = searchText?.toLowerCase().trim();
      const matchText =
        !lowerSearch ||
        (item.name && item.name.toLowerCase().includes(lowerSearch)) ||
        (item.code && item.code.toLowerCase().includes(lowerSearch));
      let matchType = true;
      if (selectedTypeIds && selectedTypeIds.length > 0) {
        console.log(selectedTypeIds);
        const typeId = getChildNodesByParentIds(selectedTypeIds, deviceTypeData?.tree);
        matchType = typeId.includes(item.typeId!);
      }
      return matchText && matchType;
    });

    setTableDataSource(filtered);
  }, [searchText, selectedTypeIds, allData]);

  //获取所属类目数据
  const { data: rawData } = useRequest(() => apiV2WaterFlowConfigTreePost({}), { refreshDeps: [] });
  const deviceTypeData = useMemo<DeviceTypeTree | undefined>(() => {
    return rawData ? adaptToDeviceTypeTree(rawData) : undefined;
  }, [rawData]);

  const deleteConfig = (id: number) => {
    const newIds = selectedRowKeys.filter(pre => pre !== id);
    const tableRows = tableDataSource?.filter(i => newIds.includes(i.id!));
    const allRows = allData?.filter(i => newIds.includes(i.id!));
    setSelectedRowKeys(newIds);
    setTableDataSource(tableRows);
    setAllData(allRows);
  };

  const handleModalSubmit = (allDevices: DeviceListItem[]) => {
    const deviceMap = new Map<number, DeviceListItem>();
    getValidDevices(allData).forEach(item => deviceMap.set(Number(item.id), item));
    getValidDevices(allDevices).forEach(item => deviceMap.set(Number(item.id), item));

    const validDevices = Array.from(deviceMap.values());
    const validIds = validDevices.map(item => Number(item.id));
    setTableDataSource(validDevices);
    setAllData(validDevices);
    setSelectedRowKeys(validIds);
    setTableSelectedRowKeys([]);
  };

  // 表格行选择变化
  const onTableSelectChange = (keys: Key[]) => {
    setTableSelectedRowKeys(keys);
  };
  const rowSelection = {
    selectedRowKeys: tableSelectedRowKeys,
    onChange: onTableSelectChange,
  };

  const handleCancel = () => {
    if (isEdit) {
      setIsEdit(false);
      return;
    }
    navigate('/longflow/monitoring/monitor');
  };

  const handleSubmit = async (val: V2WaterFlowConfigSavePostRequest) => {
    if (!isEdit) {
      setIsEdit(true);
      return;
    }
    setLoading(true);
    const timePeriods = form.getFieldValue('timePeriods');
    const formattedTimePeriods = timePeriods
      ?.map((period: [number | null, number | null]) => {
        const [startHour, endHour] = period;
        return {
          monitorStartTime: startHour !== null ? `${String(startHour).padStart(2, '0')}:00` : '',
          monitorEndTime: endHour !== null ? (endHour === 24 ? '24:00' : `${String(endHour).padStart(2, '0')}:00`) : '',
        };
      })
      .filter((period: any) => period.monitorStartTime && period.monitorEndTime);

    apiV2WaterFlowConfigSavePost({
      ...val,
      deviceIds: selectedRowKeys,
      timePeriods: formattedTimePeriods,
    })
      .then(() => {
        setIsEdit(false);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // 监测时段交集校验
  const checkOverlap = (currentIndex: number) => ({
    validator: (_: any, value: number | null) => {
      if (value === null || value === undefined) {
        return Promise.reject(new Error('请选择时间'));
      }

      const allPeriods = form.getFieldValue('timePeriods') || [];
      const currentPeriod = allPeriods[currentIndex];

      if (!currentPeriod || currentPeriod[0] === null || currentPeriod[1] === null) {
        return Promise.resolve();
      }

      const [start, end] = currentPeriod;

      // 检查开始时间是否小于结束时间
      if (start >= end) {
        return Promise.reject(new Error('开始时间必须小于结束时间'));
      }

      // 检查与其他时段是否重叠
      for (let i = 0; i < allPeriods.length; i++) {
        if (i === currentIndex) continue;
        const period = allPeriods[i];
        if (!period || period[0] === null || period[1] === null) continue;

        const [pStart, pEnd] = period;
        // 判断重叠：新开始 < 旧结束 且 新结束 > 旧开始
        if (start < pEnd && end > pStart) {
          return Promise.reject(
            new Error(`与时段${i + 1}(${String(pStart).padStart(2, '0')}:00-${String(pEnd).padStart(2, '0')}:00)冲突`)
          );
        }
      }

      return Promise.resolve();
    },
  });

  return (
    <Wrapper routes={[...(breadcrumbRoutes?.routes ?? [])]}>
      <FormContent narrow title="长流水配置">
        <Space size="large" style={{ margin: '15px 0' }}>
          <Space size="small">
            <Button type="primary" onClick={() => setShowModal(true)} disabled={!isEdit}>
              <PlusOutlined />
              配置监测设备
            </Button>
            <PopConfirm
              title="!确认删除？删除后不可恢复，你还要继续吗？"
              onConfirm={() => {
                const newIds = selectedRowKeys.filter(
                  id => !tableSelectedRowKeys.includes(String(id)) && !tableSelectedRowKeys.includes(Number(id))
                );
                const tableRows = tableDataSource?.filter(i => newIds.includes(i.id!));
                const allRows = allData?.filter(i => newIds.includes(i.id!));
                setSelectedRowKeys(newIds);
                setTableDataSource(tableRows);
                setAllData(allRows);
                setTableSelectedRowKeys([]);
              }}
            >
              <Button type="primary" disabled={!isEdit || tableSelectedRowKeys.length <= 0}>
                批量移除
              </Button>
            </PopConfirm>
          </Space>
          <Space size="middle">
            <Form.Item name="codeOrName" label="编号/名称" style={{ marginBottom: 0 }}>
              <Input
                placeholder="请输入"
                width={160}
                allowClear
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
              />
            </Form.Item>
            <Form.Item name="typeId" label="所属类目" style={{ marginBottom: 0, width: 280 }}>
              <Cascader
                options={formatOptionData(deviceTypeData)}
                allowClear
                fieldNames={{ children: 'child' }}
                multiple
                maxTagCount="responsive"
                value={selectedTypeIds}
                onChange={vals => setSelectedTypeIds(vals)}
                showSearch={{
                  filter: (inputValue, path) =>
                    path.some(option => (option.label as string).toLowerCase().indexOf(inputValue.toLowerCase()) > -1),
                }}
                placeholder={'请选择'}
              />
            </Form.Item>
          </Space>
        </Space>

        <Form form={form} disabled={!isEdit} onFinish={val => handleSubmit(val)}>
          <FieldTable
            rowKey="id"
            dataSource={tableDataSource}
            columns={buildColumns}
            rowSelection={rowSelection}
            scroll={{ y: 300 }}
          />

          <Col span={8}>
            <Form.Item
              label="文描设置"
              name="description"
              style={{ margin: '37px 0' }}
              initialValue={'长流水'}
              rules={[{ max: 5, message: '最多五个字符' }]}
            >
              <Input
                defaultValue="长流水"
                onBlur={e => {
                  const currentValue = e.target.value;
                  if (!currentValue || currentValue.trim() === '') {
                    form.setFieldsValue({
                      description: '长流水',
                    });
                  }
                }}
              />
            </Form.Item>
          </Col>

          <Space style={{ marginBottom: 5 }}>
            <span style={{ fontSize: 16 }}>快捷查询时段</span>
            <Tooltip title="对应设备的累积流量体积在任一监测时段里每小时用量大于0，即为疑似长流水设备；">
              <InfoCircleOutlined style={{ color: '#FAAD14 ', cursor: 'pointer', marginLeft: 5 }} />
            </Tooltip>
          </Space>

          <Form.List name="timePeriods" initialValue={[[null, null]]}>
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }, index) => (
                  <Space key={key} style={{ display: 'flex', margin: '20px 0' }}>
                    <Form.Item {...restField} label={`监测时段${index + 1}`} style={{ marginBottom: 0 }}>
                      <Space size={4}>
                        <Form.Item
                          noStyle
                          name={[name, 0]}
                          rules={[{ required: true, message: '请选择开始时间' }, checkOverlap(index)]}
                        >
                          <Select
                            style={{ width: 100 }}
                            options={hourOptions}
                            placeholder="开始时间"
                            disabled={!isEdit}
                            onChange={() => {
                              // 触发结束时间的重新验证
                              form.validateFields([['timePeriods', index, 1]]);
                            }}
                          />
                        </Form.Item>
                        <span style={{ color: 'var(--mx-text-secondary-color)' }}>-</span>
                        <Form.Item
                          noStyle
                          name={[name, 1]}
                          rules={[{ required: true, message: '请选择结束时间' }, checkOverlap(index)]}
                        >
                          <Select
                            style={{ width: 100 }}
                            options={hourOptions}
                            placeholder="结束时间"
                            disabled={!isEdit}
                            onChange={() => {
                              // 触发开始时间的重新验证
                              form.validateFields([['timePeriods', index, 0]]);
                            }}
                          />
                        </Form.Item>
                      </Space>
                    </Form.Item>
                    <PlusCircleOutlined
                      disabled={!isEdit}
                      onClick={() => {
                        if (!isEdit) return;
                        add([null, null]);
                      }}
                    />
                    {fields.length > 1 && (
                      <PopConfirm title="!确认删除？删除后不可恢复，你还要继续吗？" onConfirm={() => remove(name)}>
                        <MinusCircleOutlined disabled={!isEdit} />
                      </PopConfirm>
                    )}
                  </Space>
                ))}
              </>
            )}
          </Form.List>

          <Space size={8}>
            {isEdit ? (
              <Button type={'primary'} onClick={() => form.submit()} loading={loading}>
                保存
              </Button>
            ) : (
              <Button disabled={false} type={'primary'} onClick={() => setIsEdit(true)}>
                编辑
              </Button>
            )}
            <Button disabled={false} onClick={handleCancel}>
              {isEdit ? '取消' : '返回'}
            </Button>
          </Space>
        </Form>
      </FormContent>
      <UnitModal
        open={showModal}
        cancelModal={() => setShowModal(false)}
        ouIds={selectedRowKeys}
        onSubmit={handleModalSubmit}
      />
    </Wrapper>
  );
};
export default LongFlowMonitoringConfig;
